import { IUgcCommonDetail } from 'New/ugc/services/getUgcCommonDetail'
import { calcMaxPage } from 'Components/new/pagination'
import { emojiResourceList } from 'Constants/emoji'
import { IComment } from 'New/ugc/services/getComment'
import Title, { removeSplitSymbol } from 'lib/tdk-title'

interface IParams {
  article?: IUgcCommonDetail
  page?: number
  total: number
}

interface ITDK {
  title: string
  description: string
  keywords: string
  h1?: string
}

/** 移除内容中的 emoji */
function removeEmoji(string?: string) {
  if (!string) return ''
  let temp = string
  const emojiReg =
    // eslint-disable-next-line no-misleading-character-class
    /[\uD83C|\uD83D|\uD83E][\uDC00-\uDFFF][\u200D|\uFE0F]|[\uD83C|\uD83D|\uD83E][\uDC00-\uDFFF]|[0-9|*|#]\uFE0F\u20E3|[0-9|#]\u20E3|[\u203C-\u3299]\uFE0F\u200D|[\u203C-\u3299]\uFE0F|[\u2122-\u2B55]|\u303D|[\uA9|\uAE]\u3030|\uA9|\uAE|\u3030/gi
  temp = temp
    .replace(emojiReg, '')
    .replace(/(\s*$)/g, '')
    .replace(/(&nbsp;)+/g, '')
    .trim()
  const reUnescapedHtml = /(\[[^\]]+[\]])+?/g
  const reHasUnescapedHtml = RegExp(reUnescapedHtml.source)
  return reHasUnescapedHtml.test(temp)
    ? temp.replace(reUnescapedHtml, (chr) => {
        if (emojiResourceList[chr]) {
          return ''
        }
        return chr
      })
    : temp
}

// 双字节
function isDoubleChar(char: string) {
  return /^[^x00-xff]$/.test(char)
}

export function substrSingleByte(str?: string, charLen = 0) {
  if (!str) return ''
  let strIndex = 0
  let curCharLen = 0
  while (strIndex < str.length && curCharLen < charLen) {
    const curChar = str[strIndex]
    if (isDoubleChar(curChar)) {
      curCharLen += 2
    } else {
      curCharLen += 1
    }
    strIndex++
  }
  return str.slice(0, strIndex)
}

export function isLowContent(content?: string) {
  if (!content) return true
  return (content.match(/[\u4e00-\u9fa5_a-zA-Z0-9]/g)?.length || 0) < 5
}

// 移除字符串后面的符号
function removeLast(content?: string) {
  if (!content) return ''
  return content.replace(/[^\u4e00-\u9fa5_a-zA-Z0-9]+$/, '')
}

function tdk({ article, page = 1, total }: IParams): ITDK {
  const isLong = [9, 12].includes(article?.article_sub_type as number) // 长贴
  const isWenda = article?.article_sub_type === 8
  const isVideo = article?.article_sub_type === 6
  const isKoubei = [12, 13, 14].includes(article?.article_sub_type as number) // 口碑
  const userName = article?.motor_profile_info?.name || ''
  const communityName = article?.motor_car_info?.motor_name || ''
  const pageDesc = page > 1 ? `${page}/${calcMaxPage(total, 20)}页` : ''
  const carInfo = {
    // 评价的车辆信息
    series_name:
      article?.car_view?.series_name || article?.buy_car_info?.series_name,
    car_name: article?.car_view?.car_name || article?.buy_car_info?.car_name,
    year: article?.car_view?.year_id || article?.buy_car_info?.year,
  }
  const hasCarInfo = carInfo.series_name && carInfo.car_name
  const carInfoStr = hasCarInfo
    ? `${carInfo?.series_name}${carInfo.year ? `${carInfo.year}款` : ''}${
        carInfo?.car_name
      }`
    : ''
  const motorTitle = removeEmoji(article?.motor_title)
  const threadTitle = removeEmoji(article?.thread_title)
  const articleTitle =
    (isLong || isWenda ? motorTitle : threadTitle) || motorTitle || threadTitle
  let articleContent =
    article?.content?.replace(/\{!--.*?--\}/g, '').replace(/<[^>]+>/g, '') || ''
  articleContent = removeEmoji(articleContent).replace(/&nbsp;/g, '')
  const tempTitle = articleTitle || articleContent.slice(0, 100) || ''
  const len = tempTitle.length

  const splitSymbolRemovedName = removeSplitSymbol(userName)
  const splitSymbolRemovedCommunityName = removeSplitSymbol(communityName)

  const replacedTitle = new Title(tempTitle)
    .removeSplitSymbol()
    // 多个空格替换成一个空格
    .deleteUnnecessarySpace()
    // // 末尾删除中英文句号，逗号，分号，破折号 不需要了末尾都是懂车帝
    // .replace(/(\.|。｜,｜,|;|；|-｜——)$/, '')
    // 标点前后有空格的情况
    .deleteNearSymbolSpace()
    // 中文句号改为空格
    .replaceEndSymbolWithSpace()
    // 异形括号内文字大于4删除
    .deleteBracketsData()
    // 异形括号文字小于4个，替换异形括号
    .replaceBrackets()
    // 《》,[]如果不成双就干掉
    .replaceSingleBracket()
    // 连续标点后跟省略号
    .replaceMoreAndMore()
    .replaceAllAnglePunctuation()
    .replaceHalfAnglePunctuation().title

  const title = isKoubei
    ? hasCarInfo
      ? `${removeSplitSymbol(
          carInfoStr
        )}怎么样_${splitSymbolRemovedName}_懂车帝`
      : `${removeLast(replacedTitle.slice(0, 30))}_懂车帝`
    : len < 10
    ? `${removeLast(
        replacedTitle
      )}_${splitSymbolRemovedCommunityName}_${splitSymbolRemovedName}_懂车帝`
    : len < 26
    ? `${removeLast(replacedTitle)}_${splitSymbolRemovedCommunityName}_懂车帝`
    : len < 30
    ? `${removeLast(replacedTitle)}_懂车帝`
    : `${removeLast(replacedTitle.slice(0, 30))}`

  const keywords = isKoubei
    ? hasCarInfo
      ? `${carInfoStr}怎么样,${userName},懂车帝`
      : `${title},${userName},懂车帝`
    : `${removeLast(replacedTitle.slice(0, 30))},${communityName},懂车帝`

  const description = isKoubei
    ? hasCarInfo
      ? `懂车帝${userName}提供${carInfoStr}怎么样的文章，${removeLast(
          replacedTitle.slice(0, 45)
        )}`
      : `懂车帝${userName}提供文章，${removeLast(replacedTitle.slice(0, 45))}`
    : isVideo
    ? `懂车帝用户${userName}发布了一条小视频，视频内容为：${removeLast(
        replacedTitle.slice(0, 50)
      )}`
    : `懂车帝${communityName}，提供${removeLast(replacedTitle.slice(0, 45))}`
  const h1 = isKoubei ? `${userName}点评${carInfoStr}怎么样` : ''

  return {
    title: `${title}${pageDesc ? `_${pageDesc}` : ''}`,
    description,
    keywords,
    h1,
  }
}

export default tdk

interface IAnswerParams {
  article?: IUgcCommonDetail
  answer?: IComment
}

export function answerTdk({ article, answer }: IAnswerParams): ITDK {
  const answerContent = answer?.text || ''
  const top28 = removeLast(answerContent.slice(0, 28))
  const questionAuthor = article?.motor_profile_info?.name || ''
  const answerAuthor = answer?.profile_info?.name || ''
  if (answerContent.length < 12) {
    return {
      title: `${top28}_${answerAuthor}_懂车帝`,
      keywords: `${top28},${answerAuthor},懂车帝`,
      description: `懂车帝${questionAuthor}提问${removeLast(
        article?.motor_title?.slice(0, 30)
      )}，获得${answerAuthor}答案为${removeLast(answerContent.slice(0, 30))}`,
    }
  }
  return {
    title: `${top28}_懂车帝`,
    keywords: `${top28},懂车帝`,
    description: `懂车帝${questionAuthor}提问${removeLast(
      article?.motor_title?.slice(0, 30)
    )}，获得${answerAuthor}答案为${removeLast(answerContent.slice(0, 30))}`,
  }
}
